/**
 * TenantContextBar — the persistent "which tenant am I operating on" strip
 * at the top of every tenant-plane screen (P1 of the console redesign).
 *
 * Shows: tenant chip + name + ancestry path + status tags, a star toggle,
 * and the "Switch tenant (⌘K)" trigger. The switcher itself is owned by the
 * page (it needs the tenants list + navigation), so this bar only renders
 * the trigger and forwards clicks.
 */

import { useMemo, useState } from 'react';
import { Tooltip } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { getStarredTenants, toggleStarTenant, subscribeConsoleState } from './consoleState';
import { TenantChip, SWITCHER_KEY_LABEL, type SwitcherTenant } from './TenantSwitcher';
import { useEffect } from 'react';
import './console.css';

interface Props {
  tenant: SwitcherTenant;
  /** Full tenants list — used to build the ancestry path. */
  allTenants: SwitcherTenant[];
  /** Extra status tags rendered after the name (company, enforced, …). */
  tags?: React.ReactNode;
  onOpenSwitcher: () => void;
}

export default function TenantContextBar({ tenant, allTenants, tags, onOpenSwitcher }: Props) {
  const { t } = useLanguage();
  const [, setTick] = useState(0);
  useEffect(() => subscribeConsoleState(() => setTick(n => n + 1)), []);

  const path = useMemo(() => {
    const byId = new Map(allTenants.map(x => [x.tenant_id, x]));
    const parts: string[] = [];
    let cur: SwitcherTenant | undefined = tenant;
    let guard = 0;
    while (cur && guard++ < 10) {
      parts.unshift(cur.tenant_code || cur.tenant_name);
      cur = cur.parent_tenant_id ? byId.get(cur.parent_tenant_id) : undefined;
    }
    return parts;
  }, [tenant, allTenants]);

  const starred = getStarredTenants().includes(tenant.tenant_id);

  return (
    <div className="sa-ctxbar" data-testid="tenant-context-bar">
      <TenantChip tenant={tenant} size="lg" />
      <div className="sa-ctxbar-names">
        <div className="sa-ctxbar-title">
          {tenant.tenant_name}
          {tags}
        </div>
        <div className="sa-ctxbar-path">
          {path.slice(0, -1).join(' / ')}
          {path.length > 1 && ' / '}
          <b>{path[path.length - 1]}</b>
        </div>
      </div>
      <div className="sa-ctxbar-actions">
        <Tooltip title={starred
          ? t('superAdmin.console.unstarTenant')
          : t('superAdmin.console.starTenant')}
        >
          <button
            type="button"
            className={`sa-star-btn ${starred ? 'sa-star-btn--on' : ''}`}
            onClick={() => toggleStarTenant(tenant.tenant_id)}
            aria-pressed={starred}
            data-testid="tenant-star-btn"
          >
            {starred ? '★' : '☆'}
          </button>
        </Tooltip>
        <button
          type="button"
          className="btn-secondary"
          onClick={onOpenSwitcher}
          data-testid="open-tenant-switcher"
        >
          {t('superAdmin.console.switchTenant')}{' '}
          <span className="sa-ctxbar-kbd">{SWITCHER_KEY_LABEL}</span>
        </button>
      </div>
    </div>
  );
}
